import {
  Button,
  PasswordInput,
  PinInput,
  Text,
  TextInput,
} from "@mantine/core";
import {
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import authService from "../../services/api/authService";
import { getStrength } from "../../utils/passwordStrength";
import { passwordRegex } from "../../regex/regex";

// ---------------------- Reusable, memoized UI pieces ----------------------

const Logo = memo(() => (
  <img src="/logo.png" alt="Logo" className="w-20 h-20" loading="lazy" />
));
Logo.displayName = "Logo";

const Title = memo(({ step }: { step: "email" | "verify" | "set" }) => (
  <h1 className="text-xl font-bold text-center">
    {step === "email" && "ŞİFRENİ SIFIRLA"}
    {step === "verify" && "KODU DOĞRULA VE YENİ ŞİFRE BELİRLE"}
    {step === "set" && "YENİ ŞİFRE BELİRLE"}
  </h1>
));
Title.displayName = "Title";

const Background = memo(() => (
  <div
    className="w-full h-full absolute top-0 left-0 overflow-hidden opacity-25 pointer-events-none"
    style={{ zIndex: -1 }}
  >
    <img src="/shapes/line.png" alt="" className="w-full" loading="lazy" />
  </div>
));
Background.displayName = "Background";

const EmailInput = memo(
  ({
    value,
    onChange,
    disabled,
  }: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
  }) => (
    <TextInput
      type="email"
      label="E-posta"
      required
      id="forgot-email"
      size="md"
      className="w-full"
      autoComplete="email"
      maxLength={60}
      onChange={onChange}
      value={value}
      disabled={disabled}
    />
  ),
);
EmailInput.displayName = "EmailInput";

const PasswordStrengthIndicator = memo(({ password }: { password: string }) => {
  const strength = useMemo(() => getStrength(password), [password]);
  const meterColor = strength < 50 ? "red" : strength < 75 ? "yellow" : "green";
  const label = strength < 50 ? "Zayıf" : strength < 75 ? "Orta" : "Güçlü";

  if (!password) return null;

  return (
    <div className="text-xs flex items-center justify-between">
      <span className="text-gray-500 dark:text-gray-400">
        Şifre en az 8 karakter, büyük harf, küçük harf ve rakam içermelidir.
      </span>
      <span className={`font-medium text-${meterColor}-500`}>{label}</span>
    </div>
  );
});
PasswordStrengthIndicator.displayName = "PasswordStrengthIndicator";

const PasswordFields = memo(
  ({
    password,
    confirm,
    onPasswordChange,
    onConfirmChange,
  }: {
    password: string;
    confirm: string;
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onConfirmChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <>
      <PasswordInput
        label="Yeni Şifre"
        required
        id="reset-password"
        type="password"
        size="md"
        className="w-full"
        maxLength={30}
        autoComplete="new-password"
        value={password}
        onChange={onPasswordChange}
      />
      <PasswordStrengthIndicator password={password} />

      <PasswordInput
        label="Yeni Şifre (Tekrar)"
        required
        id="reset-password-confirm"
        type="password"
        size="md"
        className="w-full"
        maxLength={30}
        autoComplete="new-password"
        value={confirm}
        onChange={onConfirmChange}
      />
    </>
  ),
);
PasswordFields.displayName = "PasswordFields";

// ---------------------- Page Component ----------------------

const ForgotPasswordPage = () => {
  const navigation = useNavigate();
  const location = useLocation();

  // step: email -> verify (kod girişi; eski akış), set (token ile direkt şifre belirleme)
  const [step, setStep] = useState<"email" | "verify" | "set">("email");
  const [loading, setLoading] = useState(false);

  // fields
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(""); // eski akış için
  const [token, setToken] = useState<string | null>(null); // linkten gelen c paramı
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // resend cooldown (eski kod akışı için)
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<number | null>(null);

  // prefill email & token yakalama
  useEffect(() => {
    const state = location.state as any;
    if (state?.email) setEmail(state.email);

    // /sifremi-unuttum?c=... veya /sign/reset-password?c=... gibi
    const params = new URLSearchParams(location.search);
    const c = params.get("c");
    if (c) {
      // decodeURIComponent zaten browser yapıyor ama güvenli olsun:
      const decoded = decodeURIComponent(c);
      setToken(decoded);
      setStep("set"); // direkt yeni şifre adımı
    }
  }, [location.state, location.search]);

  // cooldown timer
  useEffect(() => {
    if (cooldown <= 0 && cooldownRef.current) {
      window.clearInterval(cooldownRef.current);
      cooldownRef.current = null;
      return;
    }
    if (cooldown > 0 && !cooldownRef.current) {
      cooldownRef.current = window.setInterval(() => {
        setCooldown((c) => c - 1);
      }, 1000) as unknown as number;
    }
    return () => {
      if (cooldownRef.current) {
        window.clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
    };
  }, [cooldown]);

  // validation helpers
  const isEmailValid = useMemo(() => /^\S+@\S+\.\S+$/.test(email), [email]);
  const isPwdValid = useMemo(() => passwordRegex.test(password), [password]);
  const isConfirmOk = useMemo(
    () => confirm === password && confirm.length > 0,
    [confirm, password],
  );
  const isCodeValid = useMemo(() => code.length === 6, [code]);

  const canSubmitSet = useMemo(
    () => isPwdValid && isConfirmOk,
    [isPwdValid, isConfirmOk],
  );
  const canSubmitVerify = useMemo(
    () => (token ? canSubmitSet : isCodeValid && canSubmitSet),
    [token, isCodeValid, canSubmitSet],
  );

  const containerStyle = useMemo(
    () => ({
      minHeight: "calc(100vh - 60px)",
    }),
    [],
  );

  // ---------------- API HANDLERS ----------------
  // Not: Backend’inizde link bazlı akışta token (c) value’u, eski 6 haneli kodun
  // geçtiği parametreye geçiyor olabilir. Sizde zaten:
  //   await authService.forgotReset(codeOrToken, newPassword)
  // şeklinde çalışıyor gibi. Aşağıda (token || code) kullanıyorum.

  const handleSendCode = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!isEmailValid) {
        toast.error("Geçerli bir e-posta giriniz.", {
          toastId: "forgot-invalid-email",
        });
        return;
      }
      try {
        setLoading(true);
        await authService.forgot(email.trim()); // backend link gönderiyor
        toast.success("Şifre sıfırlama linki e-postanıza gönderildi.", {
          toastId: "forgot-link-sent",
        });
        // Artık kod yerine link gidiyor; yine de istersen verify adımını kullanabilirsin.
        // Ama modern akışta kullanıcı maildeki linke tıklayıp direkt 'set' adımına gelir.
        setStep("verify"); // İstersen burada "email"de kal da diyebilirsin.
        setCooldown(30);
      } catch (err: any) {
        toast.error("Link gönderilemedi. Lütfen tekrar deneyin.", {
          toastId: "forgot-send-fail",
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [email, isEmailValid],
  );

  const handleResend = useCallback(async () => {
    if (cooldown > 0) return;
    try {
      setLoading(true);
      await authService.forgot(email.trim());
      toast.success("Link tekrar gönderildi.", {
        toastId: "forgot-link-resent",
      });
      setCooldown(30);
    } catch (err: any) {
      toast.error("Link tekrar gönderilemedi.", {
        toastId: "forgot-resend-fail",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [email, cooldown]);

  const handleResetPassword = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Token geldiyse code kontrolü yapma; gelmediyse (eski akış) 6 hane şartı uygula
      if (!token && !isCodeValid) {
        toast.error("6 haneli kodu giriniz.", {
          toastId: "forgot-invalid-code",
        });
        return;
      }
      if (!isPwdValid) {
        toast.error("Şifre kriterlerini karşılayın.", {
          toastId: "forgot-invalid-password",
        });
        return;
      }
      if (!isConfirmOk) {
        toast.error("Şifreler uyuşmuyor.", { toastId: "forgot-mismatch" });
        return;
      }

      try {
        setLoading(true);
        await authService.forgotReset((token || code).trim(), password.trim());

        toast.success(
          "Şifre başarıyla güncellendi. Giriş sayfasına yönlendiriliyorsunuz…",
          { toastId: "forgot-success" },
        );

        setTimeout(() => {
          navigation("/giris-yap", { replace: true });
        }, 1000);
      } catch (err: any) {
        toast.error("Bağlantı/kod hatalı veya süresi dolmuş olabilir.", {
          toastId: "forgot-reset-fail",
        });
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [token, code, password, isPwdValid, isConfirmOk, isCodeValid, navigation],
  );

  // ---------------- RENDER ----------------

  return (
    <div
      className="flex flex-col items-center justify-center px-4 relative z-10"
      style={containerStyle}
    >
      <div className="flex flex-col items-center gap-4 p-4 md:p-8 bg-background-lightAlt1/80 dark:bg-background-darkAlt2/80 rounded-xl backdrop-blur-sm w-full max-w-[800px]">
        <Logo />
        <Title step={step} />

        {/* 1) E-posta adımı (link talebi) */}
        {step === "email" && (
          <form
            onSubmit={handleSendCode}
            className="flex flex-col gap-4 w-full"
          >
            <Text className="text-center text-sm text-text-light dark:text-text-dark">
              E-posta adresinizi girin, şifre sıfırlama bağlantısını gönderelim.
            </Text>

            <EmailInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Button
              aria-label="Link Gönder"
              size="md"
              radius="md"
              color="blue"
              className="mt-2"
              type="submit"
              loading={loading}
              disabled={!isEmailValid}
            >
              Link Gönder
            </Button>

            <Link
              to="/giris-yap"
              className="text-sm text-text-light dark:text-text-dark text-center hover:underline"
            >
              Giriş sayfasına dön
            </Link>
          </form>
        )}

        {/* 2) Kod adımı — eski akışı koruyoruz (artık link gidiyorsa kullanıcı buraya nadiren gelir) */}
        {step === "verify" && !token && (
          <form
            onSubmit={handleResetPassword}
            className="flex flex-col gap-4 w-full"
          >
            <div className="flex flex-col items-center gap-2">
              <Text className="text-center text-sm text-text-light dark:text-text-dark">
                <b>{email}</b> adresine gönderilen 6 haneli kodu girin ve yeni
                şifrenizi belirleyin.
              </Text>
              <PinInput
                length={6}
                inputMode="numeric"
                id="reset-code"
                size="md"
                value={code}
                onChange={(v) => setCode(v)}
              />
              <div className="flex items-center gap-3">
                <Button
                  variant="subtle"
                  onClick={handleResend}
                  disabled={cooldown > 0 || loading}
                >
                  {cooldown > 0
                    ? `Tekrar gönder (${cooldown})`
                    : "Bağlantıyı yeniden gönder"}
                </Button>
                <Button variant="subtle" onClick={() => setStep("email")}>
                  E-posta değiştir
                </Button>
              </div>
            </div>

            <PasswordFields
              password={password}
              confirm={confirm}
              onPasswordChange={(e) => setPassword(e.target.value)}
              onConfirmChange={(e) => setConfirm(e.target.value)}
            />

            <Button
              aria-label="Şifreyi Sıfırla"
              size="md"
              radius="md"
              color="blue"
              className="mt-2"
              type="submit"
              loading={loading}
              disabled={!canSubmitVerify}
            >
              Şifreyi Sıfırla
            </Button>

            <Link
              to="/giris-yap"
              className="text-sm text-text-light dark:text-text-dark text-center hover:underline"
            >
              Giriş sayfasına dön
            </Link>
          </form>
        )}

        {/* 3) Token ile direkt yeni şifre (kullanıcı linkten geldiyse burası) */}
        {step === "set" && (
          <form
            onSubmit={handleResetPassword}
            className="flex flex-col gap-4 w-full"
          >
            <Text className="text-center text-sm text-text-light dark:text-text-dark">
              Şifre sıfırlama bağlantısı doğrulandı. Yeni şifrenizi belirleyin.
            </Text>

            <PasswordFields
              password={password}
              confirm={confirm}
              onPasswordChange={(e) => setPassword(e.target.value)}
              onConfirmChange={(e) => setConfirm(e.target.value)}
            />

            <Button
              aria-label="Şifreyi Sıfırla"
              size="md"
              radius="md"
              color="blue"
              className="mt-2"
              type="submit"
              loading={loading}
              disabled={!canSubmitSet}
            >
              Şifreyi Sıfırla
            </Button>

            <Link
              to="/giris-yap"
              className="text-sm text-text-light dark:text-text-dark text-center hover:underline"
            >
              Giriş sayfasına dön
            </Link>
          </form>
        )}
      </div>

      <Background />
    </div>
  );
};

export default memo(ForgotPasswordPage);
